import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import Swal from 'sweetalert2';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Button,
  Typography,
  Box,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  LinearProgress
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import loadingGif from '../../assets/loading.gif';
import './CustomerManagement.css';

const CustomerManagement = () => {
  const [orgName, setOrgName] = useState('N/A');
  const [customers, setCustomers] = useState([]);
  const [createEnabled, setCreateEnabled] = useState(false);
  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState({});

  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const [customerData, setCustomerData] = useState(null);
  const [customerType, setCustomerType] = useState({ value: 'Individual', label: 'Individual Customer' });
  const [customerId, setCustomerId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openEditCompanyDialog, setOpenEditCompanyDialog] = useState(false);

  const [openCompanyDialog, setOpenCompanyDialog] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    email: '',
    phoneNumber: ''
  });

  const calculateUsagePercentage = () => {
    if (customerData) {
      return Math.floor((customerData.customer_count / customerData.max_customer) * 100);
    }
    return 0;
  };

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomers = async () => {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('selectedOrg');

      if (!token || !orgId) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, { 
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });

        const { isEnabled } = orgEnabledResponse.data;

        if (!isEnabled) {
          Swal.fire({
            title: 'Organization Disabled',
            text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/unauthorized');
          });
          return;
        }

        const permissions = await axios.get(`${API_BASE_URL}/org-members/org-members-create`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (permissions.status === 403) {
          navigate('/unauthorized');
          return;
        }

        if (permissions.data) {
          if (!permissions.data.permissions.readPermission) {
            navigate('/unauthorized');
            return;
          }
          setCreateEnabled(permissions.data.permissions.createPermission || false);
          setUpdateEnabled(permissions.data.permissions.updatePermission || false);
          setDeleteEnabled(permissions.data.permissions.deletePermission || false);
        }

        const orgResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/organization-name`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (orgResponse.status === 403) {
          navigate('/unauthorized');
          return;
        }
        setOrgName(orgResponse.data.orgName || 'N/A');

        const allCustomers = await axios.get(`${API_BASE_URL}/customers/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedOrg: orgId,
          },
        });

        const { customers } = allCustomers.data;
        setCustomers(customers || []);

      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchAdminStatus = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        navigate('/unauthorized');
        return;
      }

      try {
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        const { isEnabled } = orgEnabledResponse.data;

        if (!isEnabled) {
          Swal.fire({
            title: 'Organization Disabled',
            text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
            icon: 'error',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate('/unauthorized');
          });
          return;
        }

        const response = await fetch(`${API_BASE_URL}/customers/customer-data`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: localStorage.getItem('selectedOrg'),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch organization info');
        }

        const data = await response.json();
        setCustomerData(data);
      } catch (error) {
        console.error('Error fetching admin status:', error);
        navigate('/unauthorized');
      }
    };
    
    fetchAdminStatus();
    fetchCustomers();
  }, [navigate]);

  const handleCompanyDialogOpen = () => {
    setOpenCompanyDialog(true);
  };

  const handleCompanyDialogClose = () => {
    setOpenCompanyDialog(false);
    setCompanyForm({ name: '', email: '', phoneNumber: '' });
  };

  const handleCompanyInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyForm({ ...companyForm, [name]: value });
  };

  const handleCompanySubmit = async () => {
    const { name, email, phoneNumber } = companyForm;
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    setOpenCompanyDialog(false);

    if (!name || !email || !phoneNumber) {
      Swal.fire({
        title: 'Error!',
        text: 'Please fill in all fields.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    console.log(companyId, name, email, phoneNumber); // debug

    try {
      const response = await axios.post(
        `${API_BASE_URL}/customers/create/${companyId}`,
        {
          customerName: name,
          customerEmail: email,
          customerNumber: phoneNumber,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
            'Content-Type': 'application/json',
          },
        }
      );
  
      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Company Customer created successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          window.location.reload();
        });
      }
    } catch (error) {
      console.error('Error creating company customer:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to create company customer. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setCompanyId('');
      setCompanyForm({ name: '', email: '', phoneNumber: '' });
    }
  }



  // todo
  const handleCreateSubCustomer = async (companyId) => {
    
  
    if (!companyId) {
      Swal.fire({
        title: 'Error!',
        text: 'Company ID is required to create a sub-customer.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    setCompanyId(companyId);

    setOpenCompanyDialog(true);

    /*
  
    const { value: formValues } = await Swal.fire({
      title: 'Enter New Company Customer Details',
      html: `
        <input id="swal-input1" class="swal2-input" placeholder="Name">
        <input id="swal-input2" class="swal2-input" placeholder="Email">
        <input id="swal-input3" class="swal2-input" placeholder="Phone Number">
      `,
      focusConfirm: false,
      showCancelButton: true,
      preConfirm: () => {
        const name = document.getElementById('swal-input1').value;
        const email = document.getElementById('swal-input2').value;
        const phoneNumber = document.getElementById('swal-input3').value;
  
        if (!name || !email || !phoneNumber) {
          Swal.showValidationMessage('Please fill out all fields');
          return null;
        }
  
        return { name, email, phoneNumber };
      },
    });
  
    if (!formValues) return;
  
    
    */
  };
  
  const handleEditCustomer = (customerId, companyId) => {
    // Individual customer, no company id
    if (customerId && customers.find(customer => customer.customer_id === customerId)?.company_id === null) {
      setOpenEditDialog(true);
      setCustomerId(customerId);
      setContactName(customers.find(customer => customer.customer_id === customerId).customer_name);
      setContactNumber(customers.find(customer => customer.customer_id === customerId).customer_number);
      setContactEmail(customers.find(customer => customer.customer_id === customerId).customer_email);
    } else if (Object.values(expandedCustomers).flat().find(customer => customer.customer_id === customerId)) {
      // Company customer (sub customer)
      const foundCustomer = Object.values(expandedCustomers).flat().find(customer => customer.customer_id === customerId);
      setOpenEditDialog(true);
      setCustomerId(customerId);
      setContactName(foundCustomer?.customer_name);
      setContactNumber(foundCustomer?.customer_number);
      setContactEmail(foundCustomer?.customer_email);
    } else {
      // Company customer
      setOpenEditCompanyDialog(true);
      setCompanyId(companyId);
      setCompanyName(customers.find(customer => customer.company_id === companyId)?.company_name || '');
      setCompanyNumber(customers.find(customer => customer.company_id === companyId)?.company_number || '');
      setCompanyEmail(customers.find(customer => customer.company_id === companyId)?.company_email || '');
      setCompanyWebsite(customers.find(customer => customer.company_id === companyId)?.company_website || '');
    }
    
  };

  const handleUpdateCustomer = async () => {
    if (!validateFields()) {
      Swal.fire({
        title: 'Error!',
        text: 'Please fill out all required fields.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    setOpenEditDialog(false);
  
    try {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('selectedOrg');
  
      const payload = {
            customerId,
            customerName: contactName,
            customerNumber: contactNumber,
            customerEmail: contactEmail,
            customerType,
          };
  
      const response = await axios.post(`${API_BASE_URL}/customers/update`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
          'Content-Type': 'application/json',
        },
      });
  
      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Customer updated successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          window.location.reload();
        });
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update customer.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const validateCompanyFields = () => {
    return (
      companyName && companyNumber && companyEmail
    );
  };

  const handleUpdateCompany = async () => {
    if (!validateCompanyFields()) {
      Swal.fire({
        title: 'Error!',
        text: 'Please fill out all required fields.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    setOpenEditCompanyDialog(false);
  
    try {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('selectedOrg');
  
      const payload = {
          companyId,
          companyName,
          companyNumber,
          companyEmail,
          companyWebsite: companyWebsite || ""
        }
  
      const response = await axios.post(`${API_BASE_URL}/customers/update`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          SelectedOrg: orgId,
          'Content-Type': 'application/json',
        },
      });
  
      if (response.status === 201) {
        Swal.fire({
          title: 'Success!',
          text: 'Company updated successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          window.location.reload();
        });
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      Swal.fire({
        title: 'Error!',
        text: 'Failed to update customer.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };
  
  const handleDeleteCustomer = async (customerId, customerName) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('selectedOrg');

    const result = await Swal.fire({
      title: `Are you sure you want to delete "${customerName}"?`,
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(`${API_BASE_URL}/customers/delete`, {
          customerId: customerId,
          customerName: customerName,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });
  
        Swal.close();
        
        if (response.status === 201) {
          Swal.fire({
            title: 'Success!',
            text: 'Customer deleted successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            window.location.reload();
          });
        }
      } catch (error) {
        console.error('Failed to delete customer:', error);
        Swal.close();
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete customer. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          window.location.reload();
        });
      }
    }
  };

  const toggleExpandCustomer = async (companyId) => {
    if (expandedCustomers[companyId]) {
      setExpandedCustomers((prevState) => {
        const newState = { ...prevState };
        delete newState[companyId];
        return newState;
      });
    } else {
      Swal.fire({
        title: 'Loading customer data...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const token = localStorage.getItem('token');
        const orgId = localStorage.getItem('selectedOrg');

        const response = await axios.get(`${API_BASE_URL}/customers/by-company/${companyId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });

        const companyCustomers = Array.isArray(response.data.customers)
          ? response.data.customers
          : [response.data.customers];

        setExpandedCustomers((prevState) => ({
          ...prevState,
          [companyId]: companyCustomers,
        }));

      } catch (error) {
        console.error('Failed to fetch company customers:', error);
        setExpandedCustomers((prevState) => ({
          ...prevState,
          [companyId]: [],
        }));
      } finally {
        Swal.close();
      }
    }
  };

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const validateFields = () => {
    if (customerType.value === 'Individual') {
      return contactName && contactNumber && contactEmail;
    } else if (customerType.value === 'Company') {
      return (
        contactName && contactNumber && contactEmail &&
        companyName && companyNumber && companyEmail
      );
    }
    return false;
  };

  const handleCreateCustomer = async () => {
    if (!validateFields()) {
        setErrorMsg('Please fill out all required fields.');
        return;
    }

    handleCloseCreateDialog();

    try {
        const token = localStorage.getItem('token');
        const orgId = localStorage.getItem('selectedOrg');

        const payload = {
            contactName,
            contactNumber,
            contactEmail,
            customerType: customerType.value,
            ...(customerType.value === 'Company' && {
                companyName,
                companyNumber,
                companyEmail,
                companyWebsite: companyWebsite || "",
            }),
        };

        // Check for duplicate customers
        const duplicateResponse = await axios.post(`${API_BASE_URL}/customers/check-duplicate`, payload, {
            headers: {
                Authorization: `Bearer ${token}`,
                SelectedOrg: orgId,
                'Content-Type': 'application/json',
            },
        });

        if (duplicateResponse.data.duplicates && duplicateResponse.data.duplicates.length > 0) {
          handleCloseCreateDialog();
          const duplicateDetails = duplicateResponse.data.duplicates
              .map(dup => {
                  // Join the matching fields into a comma-separated string, or display 'No matching fields' if none
                  const matchingFields = dup.matchingFields ? dup.matchingFields.join(', ') : 'No matching fields specified';
                  
                  // Ensure each customer ID and the matching fields appear on new lines
                  return `Customer ID: ${dup.customer_id}\nDuplicate fields: ${matchingFields}.\n`;
              })
              .join('\n');  // Ensure each duplicate customer block is separated by a newline
      
          // Use SweetAlert to show the duplicate message
          const confirmResult = await Swal.fire({
              title: 'Duplicate Found!',
              text: `A customer with similar information already exists. Details:\n\n${duplicateDetails}\n\nDo you still want to create this customer?`,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonText: 'Yes, create anyway',
              cancelButtonText: 'Cancel',
          });
      
          if (!confirmResult.isConfirmed) {
              return;
          }
        }
      

        // Proceed to create the customer if no duplicates are found or user confirmed
        const createResponse = await axios.post(`${API_BASE_URL}/customers/create`, payload, {
            headers: {
                Authorization: `Bearer ${token}`,
                SelectedOrg: orgId,
                'Content-Type': 'application/json',
            },
        });

        if (createResponse.status === 201) {
            Swal.fire({
                title: 'Success!',
                text: 'Customer created successfully!',
                icon: 'success',
                confirmButtonText: 'OK',
            }).then(() => {
                window.location.reload();
            });
        }
    } catch (error) {
        console.error('Error creating customer:', error);

        const errorMessage = error.response?.data?.message || 'Failed to create customer.';

        Swal.fire({
            title: 'Error!',
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK',
        });
      } finally {
        setCustomerType('Individual');
        setContactName('');
        setContactNumber('');
        setContactEmail('');
        setCompanyName('');
        setCompanyNumber('');
        setCompanyEmail('');
        setCompanyWebsite('');
        setErrorMsg('');
      }
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setCustomerId('');
    setContactName('');
    setContactNumber('');
    setContactEmail('');
  };

  const handleCloseEditCompanyDialog = () => {
    setOpenEditCompanyDialog(false);
    setCompanyId('');
    setCompanyName('');
    setCompanyNumber('');
    setCompanyEmail('');
    setCompanyWebsite('');
  };

  if (loading) {
    return (
      <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#fff',
      }}>
          <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
      </div>
    );
  }

  return (
    <div className="organizations-container">
      <div className="organizations-header">
        <Typography variant="h4">Customer Management: {orgName}</Typography>
        {createEnabled && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleOpenCreateDialog}
          >
            Create Customer
          </Button>
        )}
      </div>

      {loading ? (
        <TableContainer component={Paper}>
          <Table></Table>
        </TableContainer>
      ) : customers.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone #</TableCell>
                <TableCell>Orders</TableCell>
                <TableCell>Created at</TableCell>
                <TableCell>Options</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer) => (
                <React.Fragment key={customer.company_id || customer.customer_id}>
                  <TableRow 
                    hover 
                    onClick={() => customer.company_id && toggleExpandCustomer(customer.company_id)}
                    sx={{ '& > *': { borderBottom: 'unset' } }}
                  >
                  <TableCell style={{ width: '120px' }}>
                    <Box display="flex" alignItems="center">
                      {customer.company_id && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpandCustomer(customer.company_id);
                          }}
                        >
                          {expandedCustomers[customer.company_id] ? 
                            <KeyboardArrowUp fontSize="small" /> : 
                            <KeyboardArrowDown fontSize="small" />}
                        </IconButton>
                      )}
                      <span style={{ 
                        marginLeft: customer.company_id ? '4px' : '20px',
                        fontSize: '0.8rem',
                        fontFamily: 'monospace'
                      }}>
                        {customer.customer_id || customer.company_id}
                      </span>
                    </Box>
                  </TableCell>
                    <TableCell>{customer.company_id ? 'Company' : 'Individual'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {customer.company_id ? customer.company_name : customer.customer_name}
                    </TableCell>
                    <TableCell>
                      {customer.company_id ? customer.company_email : customer.customer_email}
                    </TableCell>
                    <TableCell>
                      {customer.company_id ? customer.company_number : customer.customer_number}
                    </TableCell>
                    <TableCell>{customer.orders}</TableCell>
                    <TableCell>{new Date(customer.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditCustomer(customer.customer_id, customer.company_id);
                        }}
                      >
                        Update
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCustomer(
                            customer.company_id || customer.customer_id,
                            customer.company_id ? customer.company_name : customer.customer_name
                          );
                        }}
                      >
                        Delete
                      </Button>
                      {
                        // todo
                      }
                      {customer.company_id && createEnabled && (
                        <Button 
                          variant="outlined" 
                          size="small"
                          sx={{ ml: 1 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateSubCustomer(customer.company_id);
                          }}
                        >
                          Create
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {customer.company_id && expandedCustomers[customer.company_id] && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0 }}>
                        <Collapse in={expandedCustomers[customer.company_id]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Sub Customers
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID</TableCell>
                                  <TableCell>Name</TableCell>
                                  <TableCell>Email</TableCell>
                                  <TableCell>Phone #</TableCell>
                                  <TableCell>Orders</TableCell>
                                  <TableCell>Created At</TableCell>
                                  <TableCell>Options</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {expandedCustomers[customer.company_id].map((subCustomer) => (
                                  <TableRow key={subCustomer.customer_id}>
                                    <TableCell style={{ width: '120px', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                      {subCustomer.customer_id}
                                    </TableCell>
                                    <TableCell>{subCustomer.customer_name}</TableCell>
                                    <TableCell>{subCustomer.customer_email}</TableCell>
                                    <TableCell>{subCustomer.customer_number}</TableCell>
                                    <TableCell>{subCustomer.orders}</TableCell>
                                    <TableCell>{new Date(subCustomer.created_at).toLocaleString()}</TableCell>
                                    <TableCell>
                                      {updateEnabled && (
                                        <Button 
                                          variant="outlined" 
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditCustomer(subCustomer.customer_id, null);
                                          }}
                                        >
                                          Update
                                        </Button>
                                      )}
                                      {deleteEnabled && (
                                        <Button 
                                          variant="outlined" 
                                          size="small"
                                          color="error"
                                          sx={{ ml: 1 }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteCustomer(subCustomer.customer_id, subCustomer.customer_name);
                                          }}
                                        >
                                          Delete
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography>No customers found.</Typography>
      )}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color:'#000'}}>Create New Customer</DialogTitle>
        <DialogContent>
          {customerData != null && (
            <div style={{ marginBottom: '16px' }}>
              <Typography>Current Plan Usage: {customerData.customer_count} / {customerData.max_customer} Customer(s)</Typography>
              <LinearProgress 
                variant="determinate" 
                value={calculateUsagePercentage()} 
                style={{ height: 8, borderRadius: 4, marginTop: 8 }}
              />
              <Typography variant="caption">{calculateUsagePercentage().toFixed(0)}%</Typography>
            </div>
          )}

          <FormControl fullWidth style={{ marginBottom: '16px' }}>
            <InputLabel>Customer Type</InputLabel>
            <Select
              value={customerType.value}
              onChange={(e) => setCustomerType({ value: e.target.value, label: e.target.value === 'Individual' ? 'Individual Customer' : 'Company Customer' })}
              label="Customer Type"
              defaultValue="Individual"
            >
              <MenuItem value="Individual">Individual Customer</MenuItem>
              <MenuItem value="Company">Company Customer</MenuItem>
            </Select>
          </FormControl>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Contact Name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                margin="normal"
                required
                multiline
                rows={1}
              />
              <TextField
                fullWidth
                label="Contact Number"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                margin="normal"
                required
                multiline
                rows={1}
              />
              <TextField
                fullWidth
                label="Contact Email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                margin="normal"
                required
                multiline
                rows={1}
              />
            </div>

            {customerType.value === 'Company' && (
              <div style={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  margin="normal"
                  required
                  multiline
                  rows={1}
                />
                <TextField
                  fullWidth
                  label="Company Number"
                  value={companyNumber}
                  onChange={(e) => setCompanyNumber(e.target.value)}
                  margin="normal"
                  required
                  multiline
                  rows={1}
                />
                <TextField
                  fullWidth
                  label="Company Email"
                  value={companyEmail}
                  onChange={(e) => setCompanyEmail(e.target.value)}
                  margin="normal"
                  required
                  multiline
                  rows={1}
                />
                <TextField
                  fullWidth
                  label="Company Website"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  margin="normal"
                  multiline
                  rows={1}
                />
              </div>
            )}
          </div>
          <Typography color="error">{errorMsg}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button onClick={handleCreateCustomer} disabled={!createEnabled || (customerData.customer_count >= customerData.max_customer)} color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color:'#000'}}>Edit Customer</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Customer Name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                margin="normal"
                required
                multiline
                rows={1}
              />
              <TextField
                fullWidth
                label="Customer Number"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                margin="normal"
                required
                multiline
                rows={1}
              />
              <TextField
                fullWidth
                label="Customer Email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                margin="normal"
                required
                multiline
                rows={1}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateCustomer} color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openEditCompanyDialog} onClose={handleCloseEditCompanyDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color:'#000'}}>Edit Company</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                margin="normal"
                required
                multiline
                rows={1}
              />
              <TextField
                fullWidth
                label="Company Number"
                value={companyNumber}
                onChange={(e) => setCompanyNumber(e.target.value)}
                margin="normal"
                required
                multiline
                rows={1}
              />
              <TextField
                fullWidth
                label="Company Email"
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
                margin="normal"
                required
                multiline
                rows={1}
              />
              <TextField
                fullWidth
                label="Company Website"
                value={companyWebsite}
                onChange={(e) => setCompanyWebsite(e.target.value)}
                margin="normal"
                multiline
                rows={1}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditCompanyDialog}>Cancel</Button>
          <Button onClick={handleUpdateCompany} color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openCompanyDialog} onClose={handleCompanyDialogClose}>
        <DialogTitle sx={{ color:'#000' }}>Create Sub Customer</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Name"
            type="text"
            fullWidth
            value={companyForm.name}
            onChange={handleCompanyInputChange}
            required
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            value={companyForm.email}
            onChange={handleCompanyInputChange}
            required
            multiline
            rows={1}
          />
          <TextField
            margin="dense"
            name="phoneNumber"
            label="Phone Number"
            type="tel"
            fullWidth
            value={companyForm.phoneNumber}
            onChange={handleCompanyInputChange}
            required
            multiline
            rows={1}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCompanyDialogClose}>Cancel</Button>
          <Button onClick={handleCompanySubmit}>Submit</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CustomerManagement;
